/**
 * LeftShield Admin - MSAL Configuration
 *
 * Azure AD / Entra ID authentication configuration for platform administrators.
 * This app uses a single-tenant (corporate) configuration for Transformr staff.
 */
import type { Configuration, PopupRequest } from '@azure/msal-browser';

// Environment variables for MSAL configuration
const clientId = import.meta.env.VITE_AZURE_AD_CLIENT_ID || '';
const tenantId = import.meta.env.VITE_AZURE_AD_TENANT_ID || '';
const redirectUri = import.meta.env.VITE_AZURE_AD_REDIRECT_URI || 'http://localhost:5173';

// Validate required configuration
if (!clientId) {
  console.warn('VITE_AZURE_AD_CLIENT_ID is not set. Authentication will not work.');
}
if (!tenantId) {
  console.warn('VITE_AZURE_AD_TENANT_ID is not set. Authentication will not work.');
}

/**
 * MSAL Configuration for Azure AD authentication
 */
export const msalConfig: Configuration = {
  auth: {
    clientId,
    authority: `https://login.microsoftonline.com/${tenantId}`,
    redirectUri,
    postLogoutRedirectUri: redirectUri,
  },
  cache: {
    cacheLocation: 'localStorage',
  },
  system: {
    loggerOptions: {
      logLevel: import.meta.env.DEV ? 3 : 0, // Verbose in dev, Error in prod
      piiLoggingEnabled: false,
    },
  },
};

/**
 * Scopes required for the Admin application
 */
export const loginRequest: PopupRequest = {
  scopes: [
    'openid',
    'profile',
    'email',
    'User.Read', // Read user profile from Graph
  ],
};

/**
 * Scopes for calling the LeftShield Platform API
 */
export const apiScopes = {
  platform: [
    `api://${clientId}/Platform.Admin`,
    `api://${clientId}/Tenants.ReadWrite`,
    `api://${clientId}/Trials.Manage`,
  ],
};

/**
 * Get API token request for a specific scope set
 */
export function getApiTokenRequest(scopeKey: keyof typeof apiScopes): PopupRequest {
  return {
    scopes: apiScopes[scopeKey],
  };
}
